import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import {
  type AbrangenciaFeriado,
  type AcaoHistorico,
  type CalendarioAno,
  type CalendarioResumo,
  type EventoHistorico,
  type Feriado,
  type FeriadoInput,
  type FeriadoNacionalPreview,
  type OrigemFeriado,
  type ResultadoCopia,
  type ResultadoImportacao,
  type SimulacaoPrazo,
  type SimulacaoPrazoInput,
} from '../types/feriadosTypes';

const BASE = '/admin/holidays';

type ScopeEn = 'NATIONAL' | 'STATE' | 'MUNICIPAL';
type OriginEn = 'NATIONAL_IMPORT' | 'MANUAL';
type ActionEn =
  | 'CREATE_CALENDAR'
  | 'ADD_HOLIDAY'
  | 'EDIT_HOLIDAY'
  | 'REMOVE_HOLIDAY'
  | 'IMPORT_NATIONALS'
  | 'COPY_HOLIDAYS';

interface HolidayDTO {
  id: string;
  date: string;
  name: string;
  scope: ScopeEn;
  uf: string | null;
  municipality: string | null;
  origin: OriginEn;
}
interface CalendarDetailDTO {
  year: number;
  holidays: HolidayDTO[];
  createdAt: string;
  updatedAt: string;
}
interface CalendarSummaryDTO {
  year: number;
  holidayCount: number;
  updatedAt: string;
}
interface AuditEntryDTO {
  id: string;
  at: string;
  year: number;
  authorId: string;
  authorName: string | null;
  action: ActionEn;
  detail: Record<string, unknown>;
}
interface PreviewDTO {
  endDate: string | null;
  skippedDays: { date: string; reason: 'WEEKEND' | 'HOLIDAY'; holidayName?: string }[];
  missingCalendarYear: number | null;
}

const SCOPE_TO_EN: Record<AbrangenciaFeriado, ScopeEn> = {
  NACIONAL: 'NATIONAL',
  ESTADUAL: 'STATE',
  MUNICIPAL: 'MUNICIPAL',
};
const SCOPE_TO_PT: Record<ScopeEn, AbrangenciaFeriado> = {
  NATIONAL: 'NACIONAL',
  STATE: 'ESTADUAL',
  MUNICIPAL: 'MUNICIPAL',
};
const ORIGIN_TO_PT: Record<OriginEn, OrigemFeriado> = {
  NATIONAL_IMPORT: 'NACIONAL',
  MANUAL: 'MANUAL',
};
const ACTION_TO_PT: Record<ActionEn, AcaoHistorico> = {
  CREATE_CALENDAR: 'CRIAR_CALENDARIO',
  ADD_HOLIDAY: 'ADICIONAR_FERIADO',
  EDIT_HOLIDAY: 'EDITAR_FERIADO',
  REMOVE_HOLIDAY: 'REMOVER_FERIADO',
  IMPORT_NATIONALS: 'IMPORTAR_NACIONAIS',
  COPY_HOLIDAYS: 'COPIAR_FERIADOS',
};

async function unwrap<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error?.message || 'Não foi possível concluir a operação');
  }
  return json.data as T;
}

const toFeriado = (dto: HolidayDTO): Feriado => ({
  id: dto.id,
  data: dto.date,
  nome: dto.name,
  abrangencia: SCOPE_TO_PT[dto.scope],
  uf: dto.uf,
  municipio: dto.municipality,
  origem: ORIGIN_TO_PT[dto.origin],
});

const toCalendario = (dto: CalendarDetailDTO): CalendarioAno => ({
  ano: dto.year,
  feriados: dto.holidays.map(toFeriado),
  criadoEm: dto.createdAt,
  atualizadoEm: dto.updatedAt,
});

const toBody = (input: FeriadoInput) => ({
  date: input.data,
  name: input.nome,
  scope: SCOPE_TO_EN[input.abrangencia],
  uf: input.uf ?? null,
  municipality: input.municipio ?? null,
});

const formatarDataBR = (iso: string): string => {
  const [ano, mes, dia] = iso.split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
};

const describeEvento = (action: ActionEn, detail: Record<string, unknown>): string => {
  const holiday = (detail.holiday ?? detail.after) as { name?: string; date?: string } | undefined;
  if (holiday?.name) {
    return `${holiday.name}${holiday.date ? ` (${formatarDataBR(holiday.date)})` : ''}`;
  }
  if (action === 'COPY_HOLIDAYS') {
    return `${detail.added ?? 0} copiado(s) de ${detail.sourceYear ?? ''}`.trim();
  }
  if (action === 'CREATE_CALENDAR') return `Ano ${detail.year ?? ''}`.trim();
  return '';
};

export const feriadosService = {
  async listarCalendarios(): Promise<CalendarioResumo[]> {
    const data = await unwrap<{ items: CalendarSummaryDTO[] }>(await apiGet(`${BASE}/calendars`));
    return data.items.map((item) => ({
      ano: item.year,
      totalFeriados: item.holidayCount,
      atualizadoEm: item.updatedAt,
    }));
  },

  async obterCalendario(ano: number): Promise<CalendarioAno | null> {
    const data = await unwrap<CalendarDetailDTO | null>(await apiGet(`${BASE}/calendars/${ano}`));
    return data ? toCalendario(data) : null;
  },

  async criarCalendario(ano: number, importarNacionais: boolean): Promise<CalendarioAno> {
    const data = await unwrap<CalendarDetailDTO>(
      await apiPost(`${BASE}/calendars`, { year: ano, importNationals: importarNacionais }),
    );
    return toCalendario(data);
  },

  async adicionarFeriado(ano: number, input: FeriadoInput): Promise<CalendarioAno> {
    const data = await unwrap<CalendarDetailDTO>(
      await apiPost(`${BASE}/calendars/${ano}/holidays`, toBody(input)),
    );
    return toCalendario(data);
  },

  async editarFeriado(ano: number, id: string, input: FeriadoInput): Promise<CalendarioAno> {
    const data = await unwrap<CalendarDetailDTO>(
      await apiPut(`${BASE}/calendars/${ano}/holidays/${id}`, toBody(input)),
    );
    return toCalendario(data);
  },

  async removerFeriado(ano: number, id: string): Promise<CalendarioAno> {
    const data = await unwrap<CalendarDetailDTO>(
      await apiDelete(`${BASE}/calendars/${ano}/holidays/${id}`),
    );
    return toCalendario(data);
  },

  async previewNacionais(ano: number): Promise<FeriadoNacionalPreview[]> {
    const data = await unwrap<{ items: { date: string; name: string; alreadyExists: boolean }[] }>(
      await apiGet(`${BASE}/calendars/${ano}/nationals/preview`),
    );
    return data.items.map((item) => ({
      data: item.date,
      nome: item.name,
      jaExiste: item.alreadyExists,
    }));
  },

  async importarNacionais(ano: number, datas?: string[]): Promise<ResultadoImportacao> {
    const data = await unwrap<{ imported: number; skipped: number; calendar: CalendarDetailDTO }>(
      await apiPost(`${BASE}/calendars/${ano}/nationals/import`, datas ? { dates: datas } : {}),
    );
    return {
      calendario: toCalendario(data.calendar),
      importados: data.imported,
      ignorados: data.skipped,
    };
  },

  async copiarFeriados(anoDestino: number, anoOrigem: number): Promise<ResultadoCopia> {
    const data = await unwrap<{ added: number; skipped: number; calendar: CalendarDetailDTO }>(
      await apiPost(`${BASE}/calendars/${anoDestino}/copy`, { sourceYear: anoOrigem }),
    );
    return {
      calendario: toCalendario(data.calendar),
      copiados: data.added,
      ignorados: data.skipped,
    };
  },

  async listarHistorico(): Promise<EventoHistorico[]> {
    const data = await unwrap<{ items: AuditEntryDTO[] }>(await apiGet(`${BASE}/audit`));
    return data.items.map((item) => ({
      id: item.id,
      em: item.at,
      ano: item.year,
      autor: item.authorName ?? item.authorId,
      acao: ACTION_TO_PT[item.action],
      descricao: describeEvento(item.action, item.detail ?? {}),
    }));
  },

  async simularPrazo(input: SimulacaoPrazoInput): Promise<SimulacaoPrazo> {
    const data = await unwrap<PreviewDTO>(
      await apiPost(`${BASE}/business-days/preview`, {
        startDate: input.dataInicial,
        businessDays: input.diasUteis,
      }),
    );
    return {
      dataFinal: data.endDate,
      diasPulados: data.skippedDays.map((day) => ({
        data: day.date,
        motivo: day.reason,
        nomeFeriado: day.holidayName,
      })),
      anoSemCalendario: data.missingCalendarYear,
    };
  },
};
